import { useState } from "react";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";
import AiAssistButton from "@/components/AiAssistButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const DateReviewModal = ({ isOpen, onClose, offer, otherName, onSuccess }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const revieweeId = offer
    ? (offer.from_user_id === user?.user_id ? offer.to_user_id : offer.from_user_id)
    : null;

  const handleSubmit = async () => {
    if (!rating) {
      toast.error("Pick a star rating first");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/date-reviews", {
        offer_id: offer.offer_id,
        rating,
        comment: comment.trim() || null,
      });
      toast.success("Thanks for your review!");
      setRating(0);
      setComment("");
      onClose();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="date-review-dialog">
        <DialogHeader>
          <DialogTitle>Rate your date</DialogTitle>
          <DialogDescription>
            How was your date with {otherName || "your match"}? Your rating helps great matches stand out.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex justify-center gap-2" data-testid="star-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHovered(star)}
                onMouseLeave={() => setHovered(0)}
                data-testid={`star-${star}`}
              >
                <Star
                  className={`w-9 h-9 transition-colors ${
                    star <= (hovered || rating)
                      ? "text-amber-400 fill-amber-400"
                      : "text-white/20"
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm text-muted-foreground">
              {["", "Not great", "Could be better", "Good", "Great date", "Amazing!"][rating]}
            </p>
          )}

          <Textarea
            placeholder="Anything you'd like to share about the date? (optional)"
            className="bg-white/5 border-white/10"
            maxLength={500}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid="review-comment-input"
          />
          {rating > 0 && (
            <AiAssistButton
              context="review_comment"
              receiverId={revieweeId}
              extra={{ rating }}
              onPick={setComment}
              label="AI Wingman: help me write it"
            />
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="review-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !rating} data-testid="review-submit-btn">
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
              </>
            ) : (
              "Submit Review"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DateReviewModal;
